import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, Check, X, BadgeCheck } from "lucide-react";
import { FilterBar, inRange } from "@/components/admin/FilterBar";
import { fmtTs } from "@/components/Timeline";

const TONE = { REQUESTED: "bg-amber-50 text-amber-700", APPROVED: "bg-sky-50 text-sky-700", REJECTED: "bg-rose-50 text-rose-700", PROCESSED: "bg-emerald-50 text-emerald-700" };

export default function AdminRefunds() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [st, setSt] = useState("");
    const [dates, setDates] = useState({ from: "", to: "" });
    const load = () => api.get("/admin/refunds").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);
    const shown = (rows || []).filter((r) => (!st || r.status === st) && inRange(r.created_at, dates)
        && (!q || `${r.refund_id} ${r.booking_ref} ${r.customer_name} ${r.customer_phone} ${r.reason}`.toLowerCase().includes(q.toLowerCase())));
    const sum = (s) => (rows || []).filter((r) => r.status === s).reduce((a, r) => a + r.amount, 0);
    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Refunds</h1>
            <p className="text-sm text-navy/55">Approve or reject refund requests with a reason. Accounts marks approved refunds Processed with the UTR.</p>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {["REQUESTED", "APPROVED", "PROCESSED", "REJECTED"].map((s) => (
                    <button key={s} data-testid={`refund-kpi-${s.toLowerCase()}`} onClick={() => setSt(st === s ? "" : s)} className={`rounded-2xl border p-3 text-left transition-colors ${st === s ? "border-orange bg-orange/5" : "border-navy/10 bg-white"}`}>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-navy/45">{s}</p>
                        <p className="font-heading text-lg font-extrabold text-navy">{rupee(sum(s))}</p>
                    </button>
                ))}
            </div>
            <FilterBar testid="refund-filter" q={q} setQ={setQ} placeholder="Search refund, booking, customer…" dates={dates} setDates={setDates}
                selects={[{ key: "status", label: "All statuses", value: st, set: setSt, options: ["REQUESTED", "APPROVED", "REJECTED", "PROCESSED"] }]}
                onClear={() => { setQ(""); setSt(""); setDates({ from: "", to: "" }); }} />
            {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
            <div className="mt-4 space-y-3">
                {shown.map((r) => <RefundCard key={r.id} r={r} onChanged={load} />)}
                {rows && shown.length === 0 && <div className="rounded-2xl border border-navy/10 bg-white py-12 text-center text-sm text-navy/40">No refunds match.</div>}
            </div>
        </div>
    );
}

function RefundCard({ r, onChanged }) {
    const [reason, setReason] = useState("");
    const [utr, setUtr] = useState("");
    const [busy, setBusy] = useState(false);
    const act = async (url, body, msg) => {
        setBusy(true);
        try { await api.post(url, body); toast.success(msg); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const inp = "flex-1 rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-orange";
    return (
        <div data-testid={`refund-row-${r.refund_id}`} className="rounded-2xl border border-navy/10 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="font-heading font-bold text-navy">{r.refund_id} · {rupee(r.amount)}</p>
                    <p className="text-xs text-navy/55"><Link to={`/admin/bookings/${r.booking_id}`} className="font-bold text-orange-dark">{r.booking_ref}</Link> · {r.customer_name} · {r.customer_phone} · {r.service_name}</p>
                    <p className="mt-1 text-sm text-navy/80">{r.reason}{r.details ? ` — ${r.details}` : ""}</p>
                    {r.decision_reason && <p className="text-xs text-navy/55">Decision: {r.decision_reason}</p>}
                    {r.utr && <p className="text-xs font-semibold text-emerald-700">UTR {r.utr} · {fmtTs(r.processed_at)}</p>}
                </div>
                <div className="text-right">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${TONE[r.status]}`} data-testid={`refund-status-${r.refund_id}`}>{r.status}</span>
                    <p className="mt-1 text-[11px] text-navy/40">{fmtTs(r.created_at)} · by {r.requested_role}</p>
                </div>
            </div>
            {r.status === "REQUESTED" && (
                <div className="mt-3 flex flex-wrap gap-2">
                    <input data-testid={`refund-reason-${r.refund_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for decision" className={inp} />
                    <button data-testid={`refund-approve-${r.refund_id}`} disabled={busy} onClick={() => act(`/admin/refunds/${r.id}/decision`, { decision: "approve", reason }, "Refund approved")} className="flex items-center gap-1 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white disabled:opacity-60"><Check size={14} /> Approve</button>
                    <button data-testid={`refund-reject-${r.refund_id}`} disabled={busy} onClick={() => act(`/admin/refunds/${r.id}/decision`, { decision: "reject", reason }, "Refund rejected")} className="flex items-center gap-1 rounded-full border border-rose-300 px-4 py-2 text-xs font-bold text-rose-600 disabled:opacity-60"><X size={14} /> Reject</button>
                </div>
            )}
            {r.status === "APPROVED" && (
                <div className="mt-3 flex flex-wrap gap-2">
                    <input data-testid={`refund-utr-${r.refund_id}`} value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / bank reference" className={inp} />
                    <button data-testid={`refund-processed-${r.refund_id}`} disabled={busy} onClick={() => act(`/admin/refunds/${r.id}/processed`, { utr }, "Marked processed")} className="flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white disabled:opacity-60"><BadgeCheck size={14} /> Mark processed</button>
                </div>
            )}
        </div>
    );
}
